import { createContext, useContext, useState, useCallback, useEffect } from "react";

import { BACKEND_URL } from "@/config/runtime";

const AuthContext = createContext(null);

// Session storage key for auth state caching
const AUTH_CACHE_KEY = 'campyid_auth_cache';

// Helper to get cached auth state
const getCachedAuth = () => {
  try {
    const cached = sessionStorage.getItem(AUTH_CACHE_KEY);
    if (cached) {
      const { user, timestamp } = JSON.parse(cached);
      // Cache is valid for 5 minutes (prevents stale data)
      if (Date.now() - timestamp < 5 * 60 * 1000) {
        return user;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
};

// Helper to set cached auth state
const setCachedAuth = (user) => {
  try {
    if (user) {
      sessionStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({
        user,
        timestamp: Date.now()
      }));
    } else {
      sessionStorage.removeItem(AUTH_CACHE_KEY);
    }
  } catch (e) {
    // Ignore cache errors
  }
};

export const AuthProvider = ({ children }) => {
  // Initialize from cache for instant UI (no loading spinner on refresh)
  const cachedUser = getCachedAuth();
  const [user, setUser] = useState(cachedUser);
  const [isAuthenticated, setIsAuthenticated] = useState(!!cachedUser);
  const [loading, setLoading] = useState(!cachedUser); // Only show loading if no cache
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  const login = useCallback((userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setLoading(false);
    setCachedAuth(userData); // Cache the auth state
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setIsAuthenticated(false);
    setCachedAuth(null); // Clear cache on logout
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: "include",
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData) {
          setUser(userData);
          setIsAuthenticated(true);
          setLoading(false);
          setCachedAuth(userData);
          return true;
        }
      }
      // 401 is expected for unauthenticated users - don't log as error
    } catch (error) {
      // Only log network errors, not auth failures
      if (error.name !== 'TypeError') {
        console.error("Auth check error:", error);
      }
    }
    setUser(null);
    setIsAuthenticated(false);
    setLoading(false);
    setCachedAuth(null); // Clear cache if not authenticated
    return false;
  }, []);

  // Auto-check authentication on app load
  useEffect(() => {
    if (!initialCheckDone) {
      checkAuth().finally(() => setInitialCheckDone(true));
    }
  }, [initialCheckDone, checkAuth]);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      setCachedAuth(updated); // Update cache when user data changes
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        checkAuth,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
